/**
 * CompAuditViewsPanel — admin/editor-only compact panel on the analysis
 * page showing exactly who received a magic link, when they clicked, and
 * which tabs they spent time on.
 *
 * Data source: GET /api/admin/audit-views/{analysis_id} (returns
 * {events, summary, magic_links}).
 *
 * Design intent: dense, monospaced, scan-first. Sits above the audit
 * tabs so admins glance at engagement before opening the report itself.
 * Hidden entirely if there's no comp-audit send activity — no clutter.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles, MailCheck, MousePointerClick, RefreshCcw, Download } from 'lucide-react';
import { api } from '../../lib/api';

const TAB_LABEL = {
  'magic_link_click': 'Link click',
  'audit.overview': 'Overview',
  'audit.discoverability': 'Discoverability',
  'audit.proof': 'Proof / Peers',
  'rewrite': 'Rewrites',
  'content.researched': 'Researched posts',
  'content.pov': 'POV',
  'content.prompt': 'Prompt',
  'content.topics': 'Topics',
  'content.peer_benchmark': 'Peer benchmark',
  'presence.keywords': 'Keywords',
  'presence.themes': 'Themes',
  'presence.seven': '7-day plan',
  'presence.thirty': '30-day plan',
};

function fmtDwell(ms) {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60_000) return 'just now';
    if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`;
    if (diffMs < 86_400_000) return `${Math.round(diffMs / 3_600_000)}h ago`;
    const days = Math.round(diffMs / 86_400_000);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// -----------------------------------------------------------------------
// CSV export — client-side. Two CSVs bundled into one download in the
// spirit of "one file per click": we emit a single Recipients CSV (one
// row per magic link + engagement summary). If admins need raw events
// too, we could extend later — for now the summary is what's actionable.
function toCsvValue(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows, filename) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => toCsvValue(r[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CompAuditViewsPanel({ analysisId, canView }) {
  const [state, setState] = useState({ loading: false, data: null, err: null });
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    if (!analysisId || !canView) return;
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data } = await api.get(`/admin/audit-views/${analysisId}`);
      setState({ loading: false, data, err: null });
    } catch (err) {
      setState({
        loading: false,
        data: null,
        err: err?.response?.data?.detail || err?.message || 'Failed to load views',
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [analysisId, canView]);

  // Compute a per-recipient summary row from the events + magic_links.
  const rows = useMemo(() => {
    if (!state.data) return [];
    const byUser = new Map();
    // Seed with magic-link rows so unclicked sends still surface.
    for (const m of state.data.magic_links || []) {
      byUser.set(m.user_id, {
        user_id: m.user_id,
        email: m.email,
        sent_at: m.created_at,
        first_click_at: m.clicked_at,
        last_seen_at: m.last_clicked_at || m.clicked_at,
        click_count: m.click_count || 0,
        total_dwell_ms: 0,
        tabs: [],
        magic_id: m.id,
        expires_at: m.expires_at,
        used_at: m.used_at,
        revoked_at: m.revoked_at,
      });
    }
    for (const s of state.data.summary || []) {
      const cur = byUser.get(s.user_id) || {};
      byUser.set(s.user_id, {
        ...cur,
        user_id: s.user_id,
        email: cur.email || s.email,
        first_click_at: cur.first_click_at || s.first_click_at,
        last_seen_at: s.last_seen_at || cur.last_seen_at,
        total_dwell_ms: (s.total_dwell_ms || 0),
        tabs: s.tabs_seen || [],
      });
    }
    return Array.from(byUser.values()).sort((a, b) => {
      const ta = a.last_seen_at || a.sent_at || '';
      const tb = b.last_seen_at || b.sent_at || '';
      return tb.localeCompare(ta);
    });
  }, [state.data]);

  if (!canView) return null;

  const hasActivity = (state.data?.magic_links?.length || 0) > 0;
  if (!hasActivity && !state.loading && !state.err) return null; // hide when there's nothing

  return (
    <div
      className="mb-6 border border-primary/20 bg-primary/[0.03] rounded-xl overflow-hidden"
      data-testid="comp-audit-views-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-primary/10 bg-white/60">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-primary" />
          <span className="text-[11px] font-mono-edge tracking-[0.14em] uppercase text-primary font-semibold">
            Comp audit engagement
          </span>
          {state.data && (
            <span className="text-[11px] text-muted-foreground">
              · {rows.length} recipient{rows.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              const csvRows = rows.map((r) => ({
                email: r.email || '',
                status: r.revoked_at ? 'revoked' : r.first_click_at ? 'clicked' : 'unopened',
                sent_at: r.sent_at || '',
                first_click_at: r.first_click_at || '',
                last_seen_at: r.last_seen_at || '',
                click_count: r.click_count || 0,
                total_dwell_ms: r.total_dwell_ms || 0,
                total_dwell_human: fmtDwell(r.total_dwell_ms || 0),
                tabs_seen: (r.tabs || []).filter((t) => t !== 'magic_link_click').join('|'),
                expires_at: r.expires_at || '',
              }));
              const stamp = new Date().toISOString().slice(0, 10);
              downloadCsv(csvRows, `blayzrr-comp-audit-views-${analysisId}-${stamp}.csv`);
            }}
            disabled={state.loading || rows.length === 0}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition px-2 py-1 rounded-full disabled:opacity-40 disabled:hover:text-muted-foreground"
            title="Download recipients + engagement as CSV"
            data-testid="comp-audit-views-csv"
          >
            <Download size={12} />
            <span className="font-mono-edge tracking-wider uppercase">CSV</span>
          </button>
          <button
            type="button"
            onClick={load}
            disabled={state.loading}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition px-2 py-1 rounded-full"
            title="Reload"
            data-testid="comp-audit-views-reload"
          >
            {state.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] font-mono-edge tracking-wider uppercase text-primary hover:underline px-2 py-1"
            data-testid="comp-audit-views-toggle"
          >
            {expanded ? 'Hide' : 'Show'} rows
          </button>
        </div>
      </div>

      {state.err && (
        <div className="px-4 py-3 text-[12px] text-red-600">{state.err}</div>
      )}

      {!expanded && !state.err && rows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-primary/10 text-[12px]">
          <StatCell label="Sent" value={rows.length} />
          <StatCell
            label="Clicked"
            value={rows.filter((r) => r.first_click_at).length}
            icon={<MailCheck size={11} />}
          />
          <StatCell
            label="Total events"
            value={(state.data?.events || []).length}
            icon={<MousePointerClick size={11} />}
          />
          <StatCell
            label="Total dwell"
            value={fmtDwell(rows.reduce((sum, r) => sum + (r.total_dwell_ms || 0), 0))}
          />
        </div>
      )}

      {expanded && !state.err && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="comp-audit-views-table">
            <thead className="bg-primary/5">
              <tr className="text-left text-[10px] font-mono-edge tracking-wider uppercase text-muted-foreground">
                <th className="px-4 py-2">Recipient</th>
                <th className="px-4 py-2">Sent</th>
                <th className="px-4 py-2">Clicked</th>
                <th className="px-4 py-2">Last seen</th>
                <th className="px-4 py-2 text-right">Dwell</th>
                <th className="px-4 py-2">Tabs seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/10">
              {rows.map((r) => {
                const tabs = (r.tabs || []).filter((t) => t !== 'magic_link_click');
                const linkStatus = r.revoked_at
                  ? { label: 'revoked', tone: 'text-red-600' }
                  : r.first_click_at
                    ? { label: 'clicked', tone: 'text-emerald-600' }
                    : { label: 'unopened', tone: 'text-muted-foreground' };
                return (
                  <tr
                    key={r.user_id + (r.magic_id || '')}
                    className="hover:bg-primary/[0.04]"
                    data-testid={`comp-audit-views-row-${r.email}`}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.email || r.user_id}</div>
                      <div className={`text-[10px] font-mono-edge tracking-wider uppercase ${linkStatus.tone}`}>
                        {linkStatus.label}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{fmtWhen(r.sent_at)}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {r.first_click_at ? fmtWhen(r.first_click_at) : '—'}
                      {(r.click_count || 0) > 1 && (
                        <span className="ml-1 text-[10px] font-mono-edge text-primary">
                          ×{r.click_count}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{fmtWhen(r.last_seen_at)}</td>
                    <td className="px-4 py-2 text-right font-mono-edge">
                      {r.total_dwell_ms ? fmtDwell(r.total_dwell_ms) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      {tabs.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {tabs.map((t) => (
                            <span
                              key={t}
                              className="inline-block rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-mono-edge tracking-wide"
                            >
                              {TAB_LABEL[t] || t}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    No magic links sent for this audit yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, icon }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono-edge tracking-wider uppercase text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold text-primary mt-0.5">{value}</div>
    </div>
  );
}
